import React, { useState } from 'react';
import * as yup from 'yup';
import { Form, Input, SubmitButton } from '../../../../components/form';
import { useTranslation } from 'react-i18next';
import { useMutationErrorHandler } from '../../../../hooks/useMutationErrorHandler';
import { sendLoginLinkToCompanyEmail } from '../../../../api/jwtAuth/jwtAuthApi';
import { Alert } from '@/components/ui/alert';

import { Redirect } from 'react-router-dom';
import { routes } from '../../routes';
import { isSharedHubDomain } from '../../sharedHubDomain';

type FormData = {
  email: string;
};

const defaultValues: FormData = {
  email: '',
};

const formSchema: yup.ObjectSchema<FormData> = yup.object({
  email: yup.string().required().email(),
});

const LoginWithEmailPage: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { t } = useTranslation();
  const errorHandler = useMutationErrorHandler();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const onSubmit = async ({ email }: FormData) => {
    setIsSubmitting(true);
    try {
      await sendLoginLinkToCompanyEmail(email);
      setShowSuccess(true);
    } catch (error) {
      errorHandler(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // On the shared address the request host carries no tenant, so the server
  // couldn't scope the login-link lookup and a duplicated email could send the
  // wrong hub's link. Send the visitor to the company picker instead.
  if (isSharedHubDomain()) {
    return <Redirect to={routes.login.route.create({})} />;
  }

  return (
    <>
      <h1 className="display-4 text-center mb-3">{t('signIn.email.title')}</h1>
      <p className="text-muted text-center mb-5">
        {t('signIn.email.subTitle')}
      </p>
      {showSuccess && (
        <Alert variant="success">{t('signIn.email.success')}</Alert>
      )}
      <Form
        onSubmit={onSubmit}
        validationSchema={formSchema}
        defaultValues={defaultValues}>
        <Input
          label={t('form.general.email.label')}
          placeholder={t('form.general.email.placeholder')}
          name="email"
          autoFocus
        />
        <SubmitButton block disabled={isSubmitting}>
          {t('signIn.email.sendLinkButton')}
        </SubmitButton>
      </Form>
    </>
  );
};

export default LoginWithEmailPage;
